import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { User, LogOut, MapPin, ChevronDown, Search, Heart, Sun, Moon, Package, Mail, UserPlus, Phone, Truck, Frame } from 'lucide-react';
import { useSiteSettings } from '../../context/SiteSettingsContext';
import { useCustomerAuth } from '../../context/CustomerAuthContext';
import { categoryAPI } from '../../services/api';

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [profileOpen, setProfileOpen] = useState(false);
    const [megaOpen, setMegaOpen] = useState(null);
    const [categories, setCategories] = useState([]);
    const [mobileExpanded, setMobileExpanded] = useState(null);
    const location = useLocation();
    const navigate = useNavigate();
    const { settings } = useSiteSettings();
    const { customer, logout, profileIncomplete } = useCustomerAuth();
    const profileRef = useRef(null);
    const megaTimerRef = useRef(null);

    // Theme
    const [theme, setTheme] = useState(() => {
        if (typeof window !== 'undefined') return localStorage.getItem('theme') || 'light';
        return 'light';
    });
    useEffect(() => {
        document.documentElement.setAttribute('data-theme', theme);
        localStorage.setItem('theme', theme);
    }, [theme]);
    const toggleTheme = () => setTheme(prev => prev === 'light' ? 'dark' : 'light');

    // Fetch parent categories with subcategories
    useEffect(() => {
        categoryAPI.getAll({ active: 'true' })
            .then(res => setCategories(res.data || []))
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setMenuOpen(false);
        setProfileOpen(false);
        setMegaOpen(null);
    }, [location]);

    useEffect(() => {
        document.body.style.overflow = menuOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [menuOpen]);

    useEffect(() => {
        const handleClick = (e) => {
            if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
        };
        document.addEventListener('mousedown', handleClick);
        return () => document.removeEventListener('mousedown', handleClick);
    }, []);

    const handleLogout = () => {
        logout();
        setProfileOpen(false);
        navigate('/');
    };

    const [searchQuery, setSearchQuery] = useState('');

    const handleSearch = useCallback((e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/urunler?search=${encodeURIComponent(searchQuery.trim())}`);
            setSearchQuery('');
        }
    }, [searchQuery, navigate]);

    const parentCats = categories.filter(c => !c.parentId);

    const openMega = (id) => {
        clearTimeout(megaTimerRef.current);
        setMegaOpen(id);
    };
    const closeMega = () => {
        megaTimerRef.current = setTimeout(() => setMegaOpen(null), 120);
    };

    const staticLinks = [
        { to: '/urunler', label: 'Tüm Ürünler' },
        { to: '/hakkimizda', label: 'Hakkımızda' },
        { to: '/iletisim', label: 'İletişim' },
    ];

    return (
        <>
            {/* Top Info Bar */}
            <div className="top-bar">
                <div className="top-bar-inner">
                    <div className="top-bar-left">
                        <Phone size={12} />
                        <span>{settings?.phone || '+90 XXX XXX XX XX'}</span>
                        <span className="top-bar-sep">|</span>
                        <Mail size={12} />
                        <span>{settings?.email || 'info@xn--atlyekirpi-fcb.com.tr'}</span>
                    </div>
                    <div className="top-bar-center">
                        <Frame size={12} />
                        <span>Duvarlarınıza Karakter Katın</span>
                        <span className="top-bar-sep">|</span>
                        <Truck size={12} />
                        <span>Ücretsiz Kargo</span>
                    </div>
                    <div className="top-bar-right">
                        <button onClick={toggleTheme} title={theme === 'light' ? 'Karanlık Mod' : 'Aydınlık Mod'}>
                            {theme === 'light' ? <Moon size={13} /> : <Sun size={13} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Navbar */}
            <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
                <div className="navbar-row">
                    {/* Mobile hamburger - left */}
                    <button
                        className={`mobile-toggle ${menuOpen ? 'active' : ''}`}
                        onClick={() => setMenuOpen(!menuOpen)}
                        aria-label="Menü"
                    >
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                        <span className="hamburger-line" />
                    </button>

                    {/* Logo */}
                    <Link to="/" className="navbar-brand">
                        <img src="/logo.svg" alt="Atölye Kirpi" className="brand-icon-svg" />
                    </Link>

                    {/* Center: Search bar (wider) */}
                    <form className="navbar-search-form" onSubmit={handleSearch}>
                        <Search size={16} className="navbar-search-icon" />
                        <input
                            type="text"
                            className="navbar-search-input"
                            placeholder="Tablo, stil veya renk ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </form>

                    {/* Right: Favorites, Profile */}
                    <div className="navbar-actions">
                        <Link to="/favoriler" className={`navbar-icon-btn nav-fav-btn${!customer ? ' hide-mobile' : ''}`} title="Favoriler">
                            <Heart size={20} />
                        </Link>

                        {/* Profile - always icon, dropdown changes based on auth */}
                        <div className="profile-dropdown-wrap" ref={profileRef}>
                            <button className="navbar-icon-btn user-nav-btn" onClick={() => setProfileOpen(!profileOpen)}>
                                <User size={20} />
                            </button>
                            {profileOpen && (
                                <div className="profile-dropdown">
                                    {customer ? (
                                        <>
                                            <div className="profile-dropdown-header">
                                                <div className="profile-dropdown-avatar">{customer.firstName[0]}{customer.lastName[0]}</div>
                                                <div>
                                                    <strong>{customer.firstName} {customer.lastName}</strong>
                                                    <span>{customer.email}</span>
                                                </div>
                                            </div>
                                            <div className="profile-dropdown-divider" />
                                            {!customer.emailVerified ? (
                                                <Link to="/eposta-dogrula" className="profile-dropdown-item profile-dropdown-verify" onClick={() => setProfileOpen(false)}>
                                                    <Mail size={15} /> E-postanızı doğrulayın
                                                </Link>
                                            ) : profileIncomplete ? (
                                                <Link to="/profil-tamamla" className="profile-dropdown-item profile-dropdown-verify" onClick={() => setProfileOpen(false)}>
                                                    <User size={15} /> Profilinizi tamamlayın
                                                </Link>
                                            ) : (
                                                <>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                        <User size={15} /> Profilim
                                                    </Link>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} state={{ tab: 'orders' }}>
                                                        <Package size={15} /> Siparişlerim
                                                    </Link>
                                                    <Link to="/hesabim" className="profile-dropdown-item" onClick={() => setProfileOpen(false)} state={{ tab: 'addresses' }}>
                                                        <MapPin size={15} /> Adreslerim
                                                    </Link>
                                                </>
                                            )}
                                            <div className="profile-dropdown-divider" />
                                            <button className="profile-dropdown-item profile-dropdown-logout" onClick={handleLogout}>
                                                <LogOut size={15} /> Çıkış Yap
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <Link to="/giris" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                <User size={15} /> Giriş Yap
                                            </Link>
                                            <Link to="/kayit" className="profile-dropdown-item" onClick={() => setProfileOpen(false)}>
                                                <UserPlus size={15} /> Kayıt Ol
                                            </Link>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Category Row - horizontal scrollable on mobile */}
                <div className="navbar-categories">
                    <div className="navbar-categories-inner">
                        {parentCats.map(cat => {
                            const subs = (cat.subcategories || []).filter(s => s.isActive);
                            const isOpen = megaOpen === cat.id;
                            return (
                                <div
                                    key={cat.id}
                                    className="nav-cat-wrap"
                                    onMouseEnter={() => openMega(cat.id)}
                                    onMouseLeave={closeMega}
                                >
                                    <Link
                                        to={`/urunler?category=${cat.id}`}
                                        className={`nav-cat-link ${isOpen ? 'active' : ''}`}
                                    >
                                        {cat.name}
                                        {subs.length > 0 && <ChevronDown size={11} className={`nav-chevron ${isOpen ? 'open' : ''}`} />}
                                    </Link>
                                    {isOpen && subs.length > 0 && (
                                        <div
                                            className="nav-mega-menu"
                                            onMouseEnter={() => openMega(cat.id)}
                                            onMouseLeave={closeMega}
                                        >
                                            <div className="nav-mega-col">
                                                <Link
                                                    to={`/urunler?category=${cat.id}`}
                                                    className="nav-mega-all"
                                                >
                                                    Tüm {cat.name}
                                                </Link>
                                                {subs.map(sub => (
                                                    <Link
                                                        key={sub.id}
                                                        to={`/urunler?category=${sub.id}`}
                                                        className="nav-mega-item"
                                                    >
                                                        {sub.name}
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {staticLinks.map(item => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`nav-cat-link ${location.pathname === item.to ? 'active' : ''}`}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>
                </div>
            </nav>

            {/* Mobile Menu */}
            <div className={`mobile-menu-backdrop ${menuOpen ? 'open' : ''}`} onClick={() => setMenuOpen(false)} />
            <div className={`mobile-menu-panel ${menuOpen ? 'open' : ''}`}>
                <div className="mobile-menu-header">
                    <img src="/logo.svg" alt="Atölye Kirpi" className="mobile-menu-logo" />
                    <button className="mobile-menu-close" onClick={() => setMenuOpen(false)}>
                        <span className="close-icon">&times;</span>
                    </button>
                </div>

                {/* Mobile search */}
                <div className="mobile-menu-search">
                    <form onSubmit={(e) => { handleSearch(e); setMenuOpen(false); }}>
                        <Search size={15} className="mobile-search-icon" />
                        <input
                            type="text"
                            placeholder="Ürün ara..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </form>
                </div>

                <nav className="mobile-menu-nav">
                    <div className="mobile-menu-section">
                        <span className="mobile-menu-label">Kategoriler</span>
                        {parentCats.map((cat) => {
                            const subs = (cat.subcategories || []).filter(s => s.isActive);
                            const isExpanded = mobileExpanded === cat.id;
                            return (
                                <div key={cat.id} className="mobile-cat-group">
                                    <div className="mobile-cat-header">
                                        <Link
                                            to={`/urunler?category=${cat.id}`}
                                            className="mobile-menu-link"
                                            onClick={() => setMenuOpen(false)}
                                        >
                                            {cat.name}
                                        </Link>
                                        {subs.length > 0 && (
                                            <button
                                                className={`mobile-cat-toggle ${isExpanded ? 'open' : ''}`}
                                                onClick={() => setMobileExpanded(isExpanded ? null : cat.id)}
                                            >
                                                <ChevronDown size={14} />
                                            </button>
                                        )}
                                    </div>
                                    {isExpanded && subs.length > 0 && (
                                        <div className="mobile-sub-links">
                                            {subs.map(sub => (
                                                <Link
                                                    key={sub.id}
                                                    to={`/urunler?category=${sub.id}`}
                                                    className="mobile-sub-link"
                                                    onClick={() => setMenuOpen(false)}
                                                >
                                                    {sub.name}
                                                </Link>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mobile-menu-divider" />

                    <div className="mobile-menu-section">
                        {[
                            { to: '/urunler', label: 'Tüm Ürünler' },
                            { to: '/hakkimizda', label: 'Hakkımızda' },
                            { to: '/iletisim', label: 'İletişim' },
                        ].map((item) => (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`mobile-menu-link ${location.pathname === item.to ? 'active' : ''}`}
                                onClick={() => setMenuOpen(false)}
                            >
                                {item.label}
                            </Link>
                        ))}
                    </div>

                    {customer && (
                        <>
                            <div className="mobile-menu-divider" />
                            <div className="mobile-menu-section">
                                <Link
                                    to={!customer.emailVerified ? '/eposta-dogrula' : profileIncomplete ? '/profil-tamamla' : '/hesabim'}
                                    className="mobile-menu-link"
                                    onClick={() => setMenuOpen(false)}
                                >
                                    <User size={16} />
                                    <span>{!customer.emailVerified ? 'E-posta Doğrula' : profileIncomplete ? 'Profili Tamamla' : 'Hesabım'}</span>
                                </Link>
                            </div>
                        </>
                    )}
                </nav>

                <div className="mobile-menu-footer">
                    {customer ? (
                        <button className="mobile-logout-btn" onClick={handleLogout}>
                            <LogOut size={15} />
                            <span>Çıkış Yap</span>
                        </button>
                    ) : (
                        <Link to="/giris" className="mobile-auth-btn" onClick={() => setMenuOpen(false)}>
                            Giriş Yap
                        </Link>
                    )}
                    <button className="mobile-theme-toggle" onClick={toggleTheme} aria-label="Tema">
                        {theme === 'light' ? <Moon size={16} /> : <Sun size={16} />}
                    </button>
                </div>
            </div>
        </>
    );
}
